import {
  computed,
  onScopeDispose,
  ref,
  toValue,
  type ComputedRef,
  type MaybeRefOrGetter,
} from 'vue'

import { insightAuthoringApi, type InsightAuthoringDryRunInput } from '../api/InsightAuthoringApi'
import { AuthoringTurnFlow, type AuthoringTurnOutcome } from '../flows/AuthoringTurnFlow'
import type { ExecuteResponse } from '../models/execute.types'
import type {
  AuthoringTurn,
  InsightDraftRequest,
  InsightDraftResponse,
} from '../models/insightAuthoring.types'
import { MAX_AUTHORING_TURNS } from '../models/insightAuthoring.types'
import type { InsightConfig } from '../models/insightConfig.types'
import {
  describeAuthoringError,
  type AuthoringErrorDescriptor,
} from '../service/insightAuthoringErrors'
import {
  createInsightAuthoringTelemetry,
  type InsightAuthoringTelemetry,
} from '../service/insightAuthoringTelemetry'

import { useInsightsRbac } from './useInsightsRbac'

import { useAccount, useWorkspace } from '@/features/account'
import { ApiError } from '@/shared/http'

const ACTIVATION_WINDOW_MS = 24 * 60 * 60 * 1_000

interface InsightAuthoringPort {
  draft: (input: InsightDraftRequest, signal?: AbortSignal) => Promise<InsightDraftResponse>
  save: (sessionId: string, turnId: number, signal?: AbortSignal) => Promise<InsightConfig>
  dryRun: (
    configId: number,
    input: InsightAuthoringDryRunInput,
    signal?: AbortSignal,
  ) => Promise<ExecuteResponse>
  activate: (
    configId: number,
    targetStatus?: 'active' | 'paused',
    signal?: AbortSignal,
  ) => Promise<InsightConfig>
}

export type AuthoringRequestState = 'idle' | 'sending' | 'saving' | 'dry_running' | 'activating'

export interface UseInsightAuthoringOptions {
  sessionId?: MaybeRefOrGetter<string | undefined>
  accountId?: MaybeRefOrGetter<number | undefined>
  workspaceId?: MaybeRefOrGetter<number | string | undefined>
  canAuthor?: MaybeRefOrGetter<boolean>
  api?: InsightAuthoringPort
  flow?: AuthoringTurnFlow
  telemetry?: InsightAuthoringTelemetry
  now?: () => number
  onSessionId?: (sessionId: string) => void
  onActivated?: (configId: number) => void
}

export interface UseInsightAuthoring {
  sessionId: ComputedRef<string | null>
  turns: ComputedRef<readonly AuthoringTurn[]>
  lastDraft: ComputedRef<InsightDraftResponse['draft'] | null>
  lastDiff: ComputedRef<InsightDraftResponse['diff']>
  configId: ComputedRef<number | null>
  requestState: ComputedRef<AuthoringRequestState>
  error: ComputedRef<AuthoringErrorDescriptor | null>
  remainingTurns: ComputedRef<number>
  hasWorkspace: ComputedRef<boolean>
  canAuthor: ComputedRef<boolean>
  canSend: ComputedRef<boolean>
  canSave: ComputedRef<boolean>
  canDryRun: ComputedRef<boolean>
  canActivate: ComputedRef<boolean>
  send: (message: string) => Promise<AuthoringTurnOutcome>
  save: () => Promise<InsightConfig | null>
  dryRun: () => Promise<ExecuteResponse | null>
  activate: (targetStatus?: 'active' | 'paused') => Promise<InsightConfig | null>
  startOver: () => void
  clearError: () => void
  cancel: () => void
}

function numericId(value: string | number | undefined): number | undefined {
  if (value === undefined || String(value).trim() === '') return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

export function useInsightAuthoring(options: UseInsightAuthoringOptions = {}): UseInsightAuthoring {
  const account = options.accountId === undefined ? useAccount() : null
  const workspace = options.workspaceId === undefined ? useWorkspace() : null
  const rbac = options.canAuthor === undefined ? useInsightsRbac() : null
  const api = options.api ?? insightAuthoringApi
  const telemetry = options.telemetry ?? createInsightAuthoringTelemetry()
  const flow = options.flow ?? new AuthoringTurnFlow(api, telemetry)
  const now = options.now ?? Date.now
  const accountId = computed(() =>
    numericId(
      options.accountId === undefined ? account?.accountId.value : toValue(options.accountId),
    ),
  )
  const workspaceId = computed(() =>
    numericId(
      options.workspaceId === undefined
        ? workspace?.workspaceId.value
        : toValue(options.workspaceId),
    ),
  )
  const canAuthor = computed(() =>
    options.canAuthor === undefined
      ? (rbac?.canAuthorInsights.value ?? false)
      : toValue(options.canAuthor),
  )
  const sessionId = ref<string | null>(toValue(options.sessionId)?.trim() || null)
  const turns = ref<AuthoringTurn[]>([])
  const lastDraft = ref<InsightDraftResponse['draft'] | null>(null)
  const lastDiff = ref<InsightDraftResponse['diff']>([])
  const configId = ref<number | null>(null)
  const requestState = ref<AuthoringRequestState>('idle')
  const error = ref<AuthoringErrorDescriptor | null>(null)
  const dryRunValidUntil = ref<number | null>(null)
  let controller: AbortController | null = null
  let expiryTimer: ReturnType<typeof setTimeout> | null = null

  const hasWorkspace = computed(
    () => accountId.value !== undefined && workspaceId.value !== undefined,
  )
  const remainingTurns = computed(() => Math.max(0, MAX_AUTHORING_TURNS - turns.value.length))
  const isIdle = computed(() => requestState.value === 'idle')

  function cancel(): void {
    controller?.abort()
    controller = null
    requestState.value = 'idle'
  }

  function setExpiry(): void {
    if (expiryTimer) clearTimeout(expiryTimer)
    dryRunValidUntil.value = now() + ACTIVATION_WINDOW_MS
    expiryTimer = setTimeout(() => {
      dryRunValidUntil.value = null
      expiryTimer = null
    }, ACTIVATION_WINDOW_MS)
  }

  function begin(state: Exclude<AuthoringRequestState, 'idle'>): AbortController {
    cancel()
    const active = new AbortController()
    controller = active
    requestState.value = state
    error.value = null
    return active
  }

  function finish(active: AbortController): void {
    if (controller === active) {
      controller = null
      requestState.value = 'idle'
    }
  }

  function capture(cause: unknown): AuthoringErrorDescriptor | null {
    const normalized = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
    if (normalized.kind === 'canceled') return null
    const descriptor = describeAuthoringError(normalized)
    error.value = descriptor
    return descriptor
  }

  async function send(message: string): Promise<AuthoringTurnOutcome> {
    const active = begin('sending')
    const outcome = await flow.execute({
      sessionId: sessionId.value,
      userMessage: message,
      previousDraft: lastDraft.value,
      turnCount: turns.value.length,
      signal: active.signal,
    })
    if (controller !== active || active.signal.aborted) return { kind: 'canceled' }
    if (outcome.kind === 'received') {
      const firstSession = sessionId.value === null
      sessionId.value = outcome.sessionId
      turns.value.push(outcome.turn)
      lastDraft.value = outcome.draft
      lastDiff.value = outcome.diff
      if (firstSession) options.onSessionId?.(outcome.sessionId)
    } else if (
      outcome.kind === 'error' ||
      outcome.kind === 'invalid' ||
      outcome.kind === 'capped'
    ) {
      error.value = outcome.error
    }
    finish(active)
    return outcome
  }

  async function save(): Promise<InsightConfig | null> {
    const lastTurn = turns.value.at(-1)
    if (!sessionId.value || !lastTurn) {
      error.value = describeAuthoringError({ code: 'turn_not_found' })
      return null
    }
    const active = begin('saving')
    try {
      const config = await api.save(sessionId.value, lastTurn.turn_id, active.signal)
      if (controller !== active || active.signal.aborted) return null
      configId.value = config.id ?? null
      telemetry.trackDraftSaved({
        sessionId: sessionId.value,
        ...(config.id === undefined ? {} : { configId: config.id }),
      })
      return config
    } catch (cause) {
      capture(cause)
      return null
    } finally {
      finish(active)
    }
  }

  async function dryRun(): Promise<ExecuteResponse | null> {
    if (configId.value === null) {
      error.value = describeAuthoringError({ code: 'config_not_found' })
      return null
    }
    if (accountId.value === undefined || workspaceId.value === undefined) {
      error.value = describeAuthoringError({ code: 'workspace_not_found' })
      return null
    }
    const active = begin('dry_running')
    const startedAt = now()
    telemetry.trackDryRunStarted({ configId: configId.value, workspaceId: workspaceId.value })
    try {
      const response = await api.dryRun(
        configId.value,
        { accountId: accountId.value, workspaceId: workspaceId.value },
        active.signal,
      )
      if (controller !== active || active.signal.aborted) return null
      telemetry.trackDryRunCompleted({
        configId: configId.value,
        workspaceId: workspaceId.value,
        status: response.status,
        elapsedMs: Math.max(0, now() - startedAt),
      })
      if (response.status === 'preview_ready') setExpiry()
      return response
    } catch (cause) {
      const descriptor = capture(cause)
      if (descriptor) {
        telemetry.trackDryRunCompleted({
          configId: configId.value,
          workspaceId: workspaceId.value,
          status: 'error',
          ...(descriptor.code ? { reason: descriptor.code } : {}),
          elapsedMs: Math.max(0, now() - startedAt),
        })
      }
      return null
    } finally {
      finish(active)
    }
  }

  async function activate(
    targetStatus: 'active' | 'paused' = 'active',
  ): Promise<InsightConfig | null> {
    if (
      configId.value === null ||
      dryRunValidUntil.value === null ||
      now() >= dryRunValidUntil.value
    ) {
      dryRunValidUntil.value = null
      error.value = describeAuthoringError({ code: 'draft_not_validated' })
      return null
    }
    const active = begin('activating')
    try {
      const config = await api.activate(configId.value, targetStatus, active.signal)
      if (controller !== active || active.signal.aborted) return null
      telemetry.trackActivated({ configId: configId.value, targetStatus })
      options.onActivated?.(configId.value)
      return config
    } catch (cause) {
      capture(cause)
      return null
    } finally {
      finish(active)
    }
  }

  function startOver(): void {
    cancel()
    if (expiryTimer) clearTimeout(expiryTimer)
    expiryTimer = null
    sessionId.value = null
    turns.value = []
    lastDraft.value = null
    lastDiff.value = []
    configId.value = null
    dryRunValidUntil.value = null
    requestState.value = 'idle'
    error.value = null
  }

  onScopeDispose(() => {
    cancel()
    if (expiryTimer) clearTimeout(expiryTimer)
  })

  return {
    sessionId: computed(() => sessionId.value),
    turns: computed(() => turns.value),
    lastDraft: computed(() => lastDraft.value),
    lastDiff: computed(() => lastDiff.value),
    configId: computed(() => configId.value),
    requestState: computed(() => requestState.value),
    error: computed(() => error.value),
    remainingTurns,
    hasWorkspace,
    canAuthor,
    canSend: computed(() => canAuthor.value && isIdle.value && remainingTurns.value > 0),
    canSave: computed(() => isIdle.value && Boolean(sessionId.value && turns.value.length > 0)),
    canDryRun: computed(() => isIdle.value && hasWorkspace.value && configId.value !== null),
    canActivate: computed(
      () =>
        isIdle.value &&
        configId.value !== null &&
        dryRunValidUntil.value !== null &&
        now() < dryRunValidUntil.value,
    ),
    send,
    save,
    dryRun,
    activate,
    startOver,
    clearError: () => {
      error.value = null
    },
    cancel,
  }
}
